import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  addStateHandlers,
  flowMax,
} from 'ad-hok'
import { FC } from 'react'
import { typedAs } from 'shared/src/utils/typedAs'
import { createUseStyles } from 'react-jss'
import { addNavigationState } from 'shared/src/utils/addNavigationState'
import { addEffectOnMount, branchIfNullish } from 'ad-hok-utils'
import { addApi, addConfig } from 'shared/src/api'
import { addNavigate } from 'shared/src/utils/addNavigate'
import { ProductWithImageAndRelatedProductIds } from 'shared/src/api/types'
import { omit } from 'lodash/fp'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { hasErrors, validate, Errors } from '../../utils/validators'
import { productUrlHasDomain, productValidator } from '../account/validator'
import { Page } from '../Page'
import { NumberFieldNew, TextFieldNew } from '../TextField'
import { Button } from '../Button'
import { Preview } from './Preview'
import { BrandField } from './BrandField'
import { Brand } from './BrandSearch'
import { FormValues } from '../ProductForm'
import { Loading } from '../../utils/addCmsLoadingAndErrorIndicators'
import { centsToPriceString, priceStringToCents } from '../../utils/price'
import { addToastContext } from '../toastContext'
import { addProductLinkingContext } from '../post/productLinking'

const useStyles = createUseStyles({
  urlField: {
    marginTop: 10,
  },
  preview: {
    marginTop: 40,
    marginBottom: 39,
    alignSelf: 'flex-start',
  },
  textField: {
    marginBottom: 12,
  },
  button: {
    marginTop: 12,
  },
})

interface Input {
  productUrlId: number | undefined
  url: string
  imageId: number | undefined
  brand: Brand | undefined
  name: string
  category: string
  price: string
}

const EMPTY_INPUT: Input = {
  productUrlId: undefined,
  url: '',
  imageId: undefined,
  brand: undefined,
  name: '',
  category: '',
  price: '',
}

export const Product: FC = flowMax(
  addDisplayName('Product'),
  addNavigationState('url', 'setUrl', typedAs<string | undefined>(undefined)),
  addNavigationState(
    'productUrlId',
    'setProductUrlId',
    typedAs<number | undefined>(undefined)
  ),
  addNavigationState('productId', 'setProductId', typedAs<number | null>(null)),
  addNavigationState('name', '_', typedAs<string | null>(null)),
  addState(
    'initialInput',
    'setInitialInput',
    ({ url, productId, name }): Input | null =>
      url || productId
        ? null
        : name != null
        ? { ...EMPTY_INPUT, name }
        : EMPTY_INPUT
  ),
  addApi,
  addConfig,
  addState(
    'editingProduct',
    'setEditingProduct',
    typedAs<ProductWithImageAndRelatedProductIds | null>(null)
  ),
  addToastContext,
  addTranslationHelpers,
  addHandlers({
    showScrapingFailedErrorToast:
      ({ showErrorToast, t }) =>
      () => {
        showErrorToast({
          title: t('admin.post.errorScrapingTitle'),
          details: t('admin.post.errorScrapingDetails'),
        })
      },
    showForbiddenDomainErrorToast:
      ({ showErrorToast, t }) =>
      (domain: string) => {
        showErrorToast({
          title: t('admin.post.errorScrapingTitle'),
          details: t('admin.post.errorScrapingForbiddenDomain', { domain }),
        })
      },
  }),
  addEffectOnMount(
    ({
        config,
        url,
        setUrl,
        productUrlId,
        setProductUrlId,
        productId,
        setProductId,
        setEditingProduct,
        api,
        setInitialInput,
        showScrapingFailedErrorToast,
        showForbiddenDomainErrorToast,
      }) =>
      () => {
        if (!(url || productId)) {
          return
        }

        if (url) {
          setUrl(undefined)
          setProductUrlId(undefined)

          const getSuggestion = async () => {
            try {
              const response = await api.products.suggestionFromProductUrl(url)

              if (!response) {
                setInitialInput({
                  ...EMPTY_INPUT,
                  productUrlId,
                  url,
                })
                showScrapingFailedErrorToast()
                return
              }

              const imageId = response.image
                ? (
                    await api.images.create({
                      type: 'url',
                      value: response.image,
                    })
                  ).id
                : undefined

              setInitialInput({
                productUrlId,
                url: response.url ?? url,
                imageId,
                brand: response.brand ?? undefined,
                name: response.name ?? '',
                category: '',
                price: response.price ? (response.price / 100).toString() : '',
              })
            } catch (error) {
              showScrapingFailedErrorToast()
              setInitialInput({ ...EMPTY_INPUT, productUrlId, url })
            }
          }

          const forbiddenDomain = config.forbiddenProductUrlDomains.find(
            (domain) => productUrlHasDomain(url, domain)
          )

          if (forbiddenDomain) {
            showForbiddenDomainErrorToast(forbiddenDomain)
            setInitialInput({ ...EMPTY_INPUT, productUrlId, url })
          } else {
            getSuggestion()
          }
        } else if (productId) {
          setProductId(null)

          const getExistingProduct = async () => {
            try {
              const response = await api.products.get(productId)

              setInitialInput({
                productUrlId: undefined,
                url: response.url,
                imageId: response.imageId,
                brand: {
                  id: response.brandId,
                  name: response.brandName,
                },
                name: response.name,
                category: response.category ?? '',
                price: response.price ? centsToPriceString(response.price) : '',
              })

              setEditingProduct(response)
            } catch (error) {
              console.error('Error getting existing product', productId, error)
              setInitialInput(EMPTY_INPUT)
            }
          }

          getExistingProduct()
        }
      }
  ),
  branchIfNullish('initialInput', {
    returns: () => <Loading />,
  }),
  addStateHandlers(
    ({ initialInput }) => ({
      input: initialInput,
      formErrors: typedAs<Errors<FormValues>>({}),
    }),
    {
      onChangeUrl:
        ({ input, formErrors }) =>
        (url: string) => ({
          input: { ...input, url },
          formErrors: omit(['url'], formErrors),
        }),
      onChangeImageId:
        ({ input, formErrors }) =>
        (imageId: number | undefined) => ({
          input: { ...input, imageId },
          formErrors: omit(['imageId'], formErrors),
        }),
      onChangeBrand:
        ({ input, formErrors }) =>
        (brand: Brand | undefined) => ({
          input: { ...input, brand },
          formErrors: omit(['brandId'], formErrors),
        }),
      onChangeName:
        ({ input, formErrors }) =>
        (name: string) => ({
          input: { ...input, name },
          formErrors: omit(['name'], formErrors),
        }),
      onChangePrice:
        ({ input, formErrors }) =>
        (price: string) => ({
          input: { ...input, price },
          formErrors: omit(['price'], formErrors),
        }),
      setFormErrors: () => (formErrors: Errors<FormValues>) => ({
        formErrors,
      }),
    }
  ),
  addProps(
    ({ config, t }) => ({
      validator: productValidator(config, t, 'creator'),
    }),
    ['config', 't']
  ),
  addNavigate,
  addProductLinkingContext,
  addToastContext,
  addHandlers({
    onSave:
      ({
        input: { brand, ...input },
        validator,
        setFormErrors,
        api,
        setLinkProductId,
        navigate,
        showErrorToast,
        editingProduct,
        t,
      }) =>
      () => {
        const formErrors = validate(validator, { ...input, brandId: brand?.id })
        setFormErrors(formErrors)

        if (hasErrors(formErrors)) {
          return
        }

        const category = input.category || null

        const createProduct = async () => {
          try {
            const product = await api.products.create({
              productUrlId: input.productUrlId ?? null,
              url: input.url!,
              name: input.name!,
              imageId: input.imageId!,
              brandId: brand!.id,
              category,
              price: priceStringToCents(input.price!),
              relatedProductIds: [],
            })

            setLinkProductId(product.id)
            navigate(-1)
          } catch (error) {
            showErrorToast({
              title: t('admin.post.errorCreatingProduct'),
              details: t('admin.general.genericError'),
            })
          }
        }

        const updateProduct = async (
          editingProduct: ProductWithImageAndRelatedProductIds
        ) => {
          try {
            await api.products.update(editingProduct.id, {
              productUrlId: input.productUrlId ?? null,
              url: input.url!,
              name: input.name!,
              imageId: input.imageId!,
              brandId: brand!.id,
              category,
              price: priceStringToCents(input.price!),
              relatedProductIds: editingProduct.relatedProductIds,
            })

            navigate(-1)
          } catch (error) {
            showErrorToast({
              title: t('admin.post.errorUpdatingProduct'),
              details: t('admin.general.genericError'),
            })
          }
        }

        if (editingProduct) {
          updateProduct(editingProduct)
        } else {
          createProduct()
        }
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    input: { url, imageId, brand, name, price },
    onChangeUrl,
    onChangeImageId,
    onChangeBrand,
    onChangeName,
    onChangePrice,
    formErrors,
    onSave,
    editingProduct,
    classes,
    t,
  }) => (
    <Page
      leftButton=""
      title={
        editingProduct
          ? t('admin.product.titleEdit')
          : t('admin.product.titleCreate')
      }
    >
      <TextFieldNew
        type="url"
        label={t('admin.product.fields.url')}
        value={url}
        onChange={onChangeUrl}
        noMargin
        className={classes.urlField}
        error={formErrors.url}
        showErrorMessage
        disabled={editingProduct ? true : true}
      />
      <Preview
        imageId={imageId}
        onChangeImageId={onChangeImageId}
        brandName={brand?.name}
        name={name}
        className={classes.preview}
        error={formErrors.imageId}
      />
      <BrandField
        brand={brand}
        onChange={onChangeBrand}
        className={classes.textField}
        error={formErrors.brandId}
      />
      <TextFieldNew
        label={t('admin.product.fields.name')}
        value={name}
        onChange={onChangeName}
        noMargin
        className={classes.textField}
        error={formErrors.name}
      />
      <NumberFieldNew
        label={t('admin.product.fields.price')}
        value={price}
        onChange={onChangePrice}
        noMargin
        className={classes.textField}
        error={formErrors.price}
      />
      <Button
        type="fullWidth"
        text={editingProduct ? t('admin.product.edit') : t('admin.product.add')}
        icon={editingProduct ? undefined : 'add'}
        onClick={onSave}
        className={classes.button}
      />
    </Page>
  )
)
